// ─────────────────────────────────────────────────────────────────────────────
// FILE: src/pages/hr/LeaveManagementTabs.jsx
// ─────────────────────────────────────────────────────────────────────────────
import React, { useMemo } from "react";
import { 
  LayoutDashboard, 
  CalendarDays, 
  FileCheck, 
  Settings2,
  UsersRound, 
  ClipboardList
} from "lucide-react";
import { useTabNavigation } from "../../helpers/useTabNavigation";
import { usePermission } from "../../hooks/usePermission";
import LeaveTypesPage from "../../components/HRComponents/LeaveTypesPage";
// import LeavePoliciesPage from "../../components/HRComponents/LeavePoliciesPage";
import EmployeeBalancesList from "../../components/HRComponents/EmployeeBalancesList";
import LeaveDashboard from "../../components/HRComponents/LeaveDashboard";
import MyLeavesList from "../../components/HRComponents/MyLeavesList";
import LeaveApprovals from "../../components/HRComponents/LeaveApprovals";
import AllLeaveRequestsList from "../../components/HRComponents/AllLeaveRequestsList";

// Importing isolated component tab files cleanly
// import LeaveDashboard from "../../components/HRComponents/LeaveDashboard"; // Own balance overview
// import MyLeavesList from "../../components/HRComponents/MyLeavesList";     // Apply leave & request list
// import LeaveApprovals from "../../components/HRComponents/LeaveApprovals";   // Pending approvals list
// import LeaveTypesPage from "./LeaveTypesPage";                             // Leave types configuration CRUD
// import LeavePoliciesPage from "../../components/HRComponents/LeavePoliciesPage"; // Leave policies setup
// import EmployeeBalancesList from "../../components/HRComponents/EmployeeBalancesList"; // Initialize & accrue panel

const LeaveManagementTabs = () => {
  const { hasPermission, user } = usePermission();

  // Tab configurations matching your exact KRA structural layout
  const tabs = useMemo(() => {
    // 1. Basic tabs accessible to all logged-in employees
    const allTabs = [
      {
        id: "dashboard",
        label: "My Dashboard",
        icon: <LayoutDashboard className="h-4 w-4" />,
        // description: "View your current leave balances, running summaries, and calendars",
        content: <LeaveDashboard user={user} />
      },
      {
        id: "my-leaves",
        label: "My Leaves",
        icon: <CalendarDays className="h-4 w-4" />,
        // description: "Apply for leaves, view your request history, and cancel submissions",
        content: <MyLeavesList user={user} />
      },
    ];

    // 2. Manager and Administrative tabs restricted based on permissions
    const adminTabs = [
      {
        id: "leave-approvals",
        label: "Leave Approvals",
        icon: <FileCheck className="h-4 w-4" />,
        // description: "Approve, reject, and review team leave requests with manager notes",
        content: <LeaveApprovals user={user} />,
        permissionKey: "hr:leave-approvals",
        action: "view"
      },
      {
        // ✅ NEW TAB: Admin & Owner Global Leave Monitor logs
        id: "all-leaves",
        label: "All Leaves",
        icon: <ClipboardList className="h-4 w-4" />,
        // description: "Global logs and status reports of all leave applications across the entire organization",
        content: <AllLeaveRequestsList user={user} />,
        permissionKey: "hr:all-leaves", // Controlled via standard permission overlays [1]
        action: "view"
      },
      {
        id: "leave-types",
        label: "Leave Types",
        icon: <Settings2 className="h-4 w-4" />,
        // description: "Configure global leave parameters, validation rules, and attachments",
        content: <LeaveTypesPage user={user} />,
        permissionKey: "hr:leave-types",
        action: "view"
      },
      // {
      //   id: "leave-policies",
      //   label: "Leave Policies",
      //   icon: <Layers className="h-4 w-4" />,
      //   // description: "Bundle leave allocations, probation and payroll cycles for employee groups",
      //   content: <LeavePoliciesPage user={user} />,
      //   permissionKey: "hr:leave-policies",
      //   action: "view"
      // },
      {
        id: "employee-balances",
        label: "Employee Balances",
        icon: <UsersRound className="h-4 w-4" />,
        // description: "Initialize, adjust balances, and trigger monthly accrual engines",
        content: <EmployeeBalancesList user={user} />,
        permissionKey: "hr:employee-balances",
        action: "view"
      }
    ];

    // Push permission-restricted tabs dynamically
    adminTabs.forEach(tab => {
      if (hasPermission(tab.permissionKey, tab.action)) {
        allTabs.push(tab);
      }
    });

    return allTabs;
  }, [user, hasPermission]);

  // Handle active navigation state using your customized helper hook
  const { activeTab, changeTab } = useTabNavigation(tabs, 'dashboard');
  
  const activeTabData = tabs.find(tab => tab.id === activeTab) || tabs[0];

  return (
    <div className="">
      {/* --- TOP TAB NAVIGATION BAR (Utilizes Brand CSS and Red color accentuations) --- */}
      <div className="bg-primary shadow-md sticky top-0 z-50">
        <div className="mx-auto">
          <div className="px-4">
            <div className="flex items-center justify-between">
              <nav className="flex space-x-2 overflow-x-auto custom-scrollbar pt-2" aria-label="Tabs">
                {tabs.map((tab) => {
                  const isActive = activeTab === tab.id;
                  return (
                    <button
                      key={tab.id}
                      onClick={() => changeTab(tab.id)}
                      className={`
                        group inline-flex items-center py-3 px-6 font-bold text-[11px] uppercase tracking-[0.1em]
                        whitespace-nowrap transition-all duration-300 ease-in-out
                        ${isActive
                          ? 'bg-white text-primary rounded-t-xl transform -translate-y-px shadow-sm'
                          : 'text-red-100 hover:text-white hover:bg-red-700 rounded-t-xl'
                        }
                      `}
                    >
                      <span className="mr-2 opacity-70">{tab.icon}</span>
                      <span>{tab.label}</span>
                    </button>
                  );
                })}
              </nav>

              {/* Subtitle description display on large devices */}
              <div className="hidden lg:block text-[10px] font-bold text-red-100 uppercase tracking-widest opacity-80 italic">
                {activeTabData?.description}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* --- SUB-TAB MOUNT CONTENT AREA (Rendered with fade-in animation) --- */}
      <div className="">
        <div className="animate-in fade-in duration-500">
          {activeTabData?.content}
        </div>
      </div>
    </div>
  );
};

export default LeaveManagementTabs;